import { AttributeType } from "../../../clients/Purchaser/Attributes";
import { DATE_RANGE_PRESETS, resolveDateRange, type DateRangePresetKey } from "../../../util/dateRangePresets";
import { CustomDateRangeForm } from "../../Common/Filters";
import { MenuItem, MenuPanel, useMenuPanel } from "../../DesignSystem";
import type { AttributeFilter, DateAttributeFilter, SetAttributeFilter } from "../types";

type DateAttributeFilterSubmenuProps = {
    attributeId: number;
    // Names the field in the custom timeframe form.
    attributeName: string;
    currentFilter?: DateAttributeFilter;
    setAttributeFilter: SetAttributeFilter;
};

// The shared presets this filter offers, in menu order.
const PRESETS: readonly DateRangePresetKey[] = ["today", "week", "month", "ytd"];

function DateAttributeFilterSubmenu({ attributeId, attributeName, currentFilter, setAttributeFilter }: DateAttributeFilterSubmenuProps) {
    // Stores the dates the preset works out to as of now, rather than the preset's name.
    const applyPreset = (preset: DateRangePresetKey) => {
        const range = resolveDateRange({ preset });
        const next: AttributeFilter = {
            type: AttributeType.DATE,
            attributeId,
            operator: "between",
            value: range.start,
            secondaryValue: range.end,
        };
        setAttributeFilter(next);
    };

    return (
        <>
            {PRESETS.map(preset => (
                <MenuItem key={preset} onSelect={() => applyPreset(preset)}>
                    {DATE_RANGE_PRESETS[preset].title}
                </MenuItem>
            ))}
            <MenuPanel label="Custom Timeframe...">
                <CustomTimeframePanel
                    attributeId={attributeId}
                    attributeName={attributeName}
                    currentFilter={currentFilter}
                    setAttributeFilter={setAttributeFilter}
                />
            </MenuPanel>
        </>
    );
}

// Map currentFilter → start/end inputs:
//   between → start=value,         end=secondaryValue
//   gte     → start=value,         end=undefined
//   lte     → start=undefined,     end=value
function rangeFromFilter(filter?: DateAttributeFilter): { start?: number; end?: number } {
    switch (filter?.operator) {
        case "between":
            return { start: filter.value, end: filter.secondaryValue };
        case "gte":
            return { start: filter.value };
        case "lte":
            return { end: filter.value };
        default:
            return {};
    }
}

// The custom start/end pair, shown as a panel in place of the menu. Applying closes the menu; cancelling goes back to the presets.
function CustomTimeframePanel({ attributeId, attributeName, currentFilter, setAttributeFilter }: DateAttributeFilterSubmenuProps) {
    const panel = useMenuPanel();

    const handleChooseDates = (range: { start?: number; end?: number }) => {
        let next: AttributeFilter;
        if (range.start !== undefined && range.end !== undefined) {
            next = { type: AttributeType.DATE, attributeId, operator: "between", value: range.start, secondaryValue: range.end };
        } else if (range.start !== undefined) {
            next = { type: AttributeType.DATE, attributeId, operator: "gte", value: range.start };
        } else if (range.end !== undefined) {
            // Primary operand always lives in `value` (mirrors NUMBER).
            next = { type: AttributeType.DATE, attributeId, operator: "lte", value: range.end };
        } else {
            return;
        }
        setAttributeFilter(next);
        panel.close();
    };

    return (
        <div className="w-[26rem] max-w-full">
            <CustomDateRangeForm
                label={attributeName}
                openEnded
                allowFutureDates
                initialRange={rangeFromFilter(currentFilter)}
                didChooseDates={handleChooseDates}
                didChooseCancel={panel.back}
            />
        </div>
    );
}

export default DateAttributeFilterSubmenu;
